import { useQuery } from "react-query";
import { Button } from "../../components/Button";
import axios from "axios";
import { useRecoilValue } from "recoil";
import { authState } from "../../recoil";
import { useState } from "react";
import { Loader } from "../../components/Loader";
import { useToggleModal } from "../../hooks/useToggleModel";
import { Modal } from "../../components/modal";
import RestaurantInvoice from "../../components/restaurant/invoice";
import { useNavigate, useParams } from "react-router-dom";
import { FaMessage } from "react-icons/fa6";
import Swal from "sweetalert2";
import { RestaurantInvoiceProps } from "../../types/restaurant";

const UserOrders = () => {
  const { userId } = useParams<{ userId: string }>();
  const auth = useRecoilValue(authState);
  const [selectedOrder, setSelectedOrder] =
    useState<RestaurantInvoiceProps | null>(null);
  const [openModel, toggleModel] = useToggleModal();
  const navigate = useNavigate();

  const {
    data: orders = [],
    isLoading,
    isFetching,
  } = useQuery(
    ["Get User Orders", auth?.restaurantId, userId],
    async () => {
      const response = await axios.get("/order/cashier/user", {
        params: {
          restaurantId: auth?.restaurantId,
          userId: userId,
        },
      });
      return response.data.orders;
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const sendOrderSMS = async (orderId: string) => {
    Swal.fire({
      title: `Are you sure to send a SMS ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#21D868",
      cancelButtonColor: "#FE5960",
      confirmButtonText: "Sure",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post("/order/cashier/send-order-sms", {
            orderId,
            userId: userId,
          });

          Swal.fire({
            icon: "success",
            title: "Success",
            text: "The order SMS sent successfully.",
            confirmButtonText: "Done",
            confirmButtonColor: "#21D868",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              title: "swal2-title-custom",
              htmlContainer: "swal2-text-custom",
            },
            didOpen: () => {
              const titleEl = document.querySelector(".swal2-title");
              if (titleEl) titleEl.setAttribute("style", "color: #21D868");
              const textEl = document.querySelector(".swal2-html-container");
              if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
            },
          });
        } catch (error: any) {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text: "Failed to sent SMS.",
            confirmButtonText: "Ok",
            confirmButtonColor: "#FE5960",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              title: "swal2-title-custom",
              htmlContainer: "swal2-text-custom",
            },
            didOpen: () => {
              const titleEl = document.querySelector(".swal2-title");
              if (titleEl) titleEl.setAttribute("style", "color: #FE5960");
              const textEl = document.querySelector(".swal2-html-container");
              if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
            },
          });
        }
      }
    });
  };

  return (
    <div className="mt-6">
      {isLoading || isFetching ? (
        <div className="flex justify-center items-center pt-10">
          <Loader />
        </div>
      ) : orders?.length === 0 ? (
        <p className="text-gray-500 text-center">No Orders found.</p>
      ) : (
        <>
          {selectedOrder && (
            <Modal
              title="Order Details"
              handleClose={toggleModel}
              isOpen={openModel}
            >
              <div className="text-black w-full flex flex-col justify-center items-center">
                <div className="w-96 flex justify-start">
                  <Button
                    lightBlue
                    className="flex items-center gap-2 mt-4"
                    onClick={() => sendOrderSMS(selectedOrder?.order_id)}
                  >
                    <FaMessage className="text-lg" />
                    Share Order SMS
                  </Button>
                </div>
                <RestaurantInvoice invoice={selectedOrder} type="search" />
                <Button lightGreen className="w-96" onClick={toggleModel}>
                  Close
                </Button>
              </div>
            </Modal>
          )}

          <div className="flex items-center gap-4 mb-6">
            <Button lightBlue onClick={() => navigate(-1)}>
              Back
            </Button>
            <p className="text-xl font-bold">Orders</p>
          </div>
          <ul className="space-y-2">
            {orders?.map((order: any) => (
              <li
                key={order.id}
                className="w-full max-w-[50rem] cursor-pointer hover:bg-green-100 p-4 border bg-green-50 rounded-md shadow-sm flex flex-wrap md:flex-row justify-between items-end gap-2"
                onClick={() => {
                  setSelectedOrder(order);
                  toggleModel();
                }}
              >
                <div className="w-2/3 grid grid-cols-2 gap-2">
                  <p className="font-semibold flex items-center gap-1 col-span-2">
                    <span className="font-medium text-PrimaryGray">
                      Customer:
                    </span>{" "}
                    {order.user?.firstName} {order.user?.lastName}
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Restaurant: {order.restaurant?.name}
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Branch:{" "}
                    {
                      order.restaurant?.Branchs?.filter(
                        (branch: { id: number }) => branch.id === auth.branchId
                      )[0]?.name
                    }
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Payment: {order.payment_method}
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Order Number : {order.order_number}
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Date: {order.order_date} at {order.order_time}
                  </p>
                  <p className="text-sm font-medium text-PrimaryGray">
                    Check Number : #{order.check_number}
                  </p>{" "}
                  <p className="text-sm font-medium text-PrimaryGray">
                    Items Number: {order.order_items?.length}
                  </p>
                  {order.customer_notes && (
                    <p className="text-sm font-medium text-PrimaryGray italic mt-1">
                      Notes: {order.customer_notes}
                    </p>
                  )}
                </div>

                <div className="font-medium text-base text-right flex flex-nowrap text-nowrap items-center gap-1">
                  Total Price:{" "}
                  <span className="text-primary text-lg font-semibold">
                    {order?.total_price?.toFixed(2)}
                  </span>{" "}
                  SAR
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default UserOrders;
